import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight, Check, Users, X, LogIn } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { formatApiError, useAuth } from "../lib/AuthContext";
import { useT } from "../lib/i18n";
import { Rise } from "./Reveal";

const WEEKDAY_NL = ["Maandag","Dinsdag","Woensdag","Donderdag","Vrijdag","Zaterdag","Zondag"];
const WEEKDAY_EN = ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"];

function fmtDate(iso, lang) {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  const wd = (dt.getUTCDay() + 6) % 7; // Mon=0..Sun=6
  const wdName = (lang === "en" ? WEEKDAY_EN : WEEKDAY_NL)[wd];
  const day = dt.getUTCDate();
  const monthShort = dt.toLocaleString(lang === "en" ? "en-GB" : "nl-NL", { month: "short", timeZone: "UTC" });
  return { weekday: wdName, day, month: monthShort };
}

export default function ClassesRSVP() {
  const { lang } = useT();
  const { user, signOut, ready } = useAuth();
  const nav = useNavigate();
  // Ingelogd account is de enige boekingsbron — gasten worden naar /login gestuurd.
  const member = user && user !== false ? {
    name: user.name || `${user.first_name || ""} ${user.last_name || ""}`.trim(),
    email: user.email, phone: user.phone || "",
    first_name: user.first_name || "", last_name: user.last_name || "",
  } : null;
  const [classes, setClasses] = useState([]);
  const [mine, setMine] = useState(new Set());
  const [myWaitlist, setMyWaitlist] = useState(new Map()); // instance_id -> position
  const [entitlement, setEntitlement] = useState(null);
  const [busy, setBusy] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchClasses = useCallback(async () => {
    try {
      const { data } = await api.get("/classes/upcoming?days_ahead=7");
      setClasses(data.items || []);
    } catch (e) {
      // silent
    }
    setLoading(false);
  }, []);

  const fetchMine = useCallback(async (email) => {
    if (!email) return;
    try {
      const [mineRes, entRes, wlRes] = await Promise.all([
        api.get(`/classes/rsvp/mine?email=${encodeURIComponent(email)}`),
        api.get(`/classes/entitlement?email=${encodeURIComponent(email)}`),
        api.get(`/classes/waitlist/mine?email=${encodeURIComponent(email)}`),
      ]);
      setMine(new Set((mineRes.data.items || []).map(x => x.instance_id)));
      setEntitlement(entRes.data);
      const wlMap = new Map();
      (wlRes.data.items || []).forEach(x => wlMap.set(x.instance_id, x.position));
      setMyWaitlist(wlMap);
    } catch {}
  }, []);

  useEffect(() => { fetchClasses(); }, [fetchClasses]);
  useEffect(() => { if (member?.email) fetchMine(member.email); }, [member, fetchMine]);

  const requireLogin = () => {
    toast(lang === "en" ? "Log in to book a class." : "Log in om een les te boeken.", { icon: <LogIn size={14}/> });
    nav(`/login?next=${encodeURIComponent("/#rsvp")}`);
  };

  const doRsvp = async (instance_id, m) => {
    setBusy(instance_id);
    try {
      const parts = (m.name || "").trim().split(/\s+/);
      const first_name = m.first_name || parts[0] || "";
      const last_name = m.last_name || parts.slice(1).join(" ") || "-";
      const { data } = await api.post("/classes/rsvp", {
        instance_id, first_name, last_name, email: m.email, phone: m.phone || null,
      });
      if (data.status === "confirmed" || data.status === "already_rsvpd") {
        setMine(prev => new Set(prev).add(instance_id));
        setMyWaitlist(prev => { const n = new Map(prev); n.delete(instance_id); return n; });
        setClasses(prev => prev.map(c => c.id === instance_id ? { ...c, going: (c.going || 0) + (data.status === "confirmed" ? 1 : 0) } : c));
        if (data.entitlement) setEntitlement(data.entitlement);
        let msg = lang === "en" ? "You're on the list. See you there." : "Je staat op de lijst. Tot dan.";
        if (data.credit_type === "membership") msg += lang === "en" ? " (Membership)" : " (Membership)";
        else if (data.credit_type && data.credit_type.startsWith("joh_pack")) msg += lang === "en" ? " (1 class deducted)" : " (1 les afgetrokken)";
        else if (data.credit_type === "joh_dropin") msg += lang === "en" ? " (drop-in used)" : " (drop-in gebruikt)";
        toast.success(msg);
      } else if (data.status === "waitlisted") {
        setMyWaitlist(prev => { const n = new Map(prev); n.set(instance_id, data.position); return n; });
        toast.success(lang === "en"
          ? `You're on the waitlist (#${data.position}). We'll email you if a spot opens.`
          : `Je staat op de reservelijst (#${data.position}). We mailen zodra er een plek vrijkomt.`);
      }
    } catch (err) {
      if (err?.response?.status === 401) { setBusy(null); return requireLogin(); }
      if (err?.response?.status === 403 && err?.response?.data?.detail === "membership_pending_approval") {
        setBusy(null);
        return toast.info(lang === "en" ? "Your membership is pending approval." : "Je membership wacht nog op goedkeuring.");
      }
      if (err?.response?.status === 402) {
        setBusy(null);
        return toast.error(lang === "en" ? "No active package or credits — choose a package first." : "Geen actief pakket of credits — kies eerst een pakket.", {
          action: { label: lang === "en" ? "Choose a package" : "Kies een pakket", onClick: () => nav("/join") },
        });
      }
      const msg = formatApiError(err?.response?.data?.detail);
      toast.error(msg || (lang === "en" ? "RSVP failed" : "Aanmelden mislukt"));
    }
    setBusy(null);
  };

  const cancelRsvp = async (instance_id) => {
    if (!member?.email) return;
    setBusy(instance_id);
    try {
      const { data } = await api.delete(`/classes/rsvp?instance_id=${encodeURIComponent(instance_id)}&email=${encodeURIComponent(member.email)}`);
      setMine(prev => { const next = new Set(prev); next.delete(instance_id); return next; });
      setMyWaitlist(prev => { const n = new Map(prev); n.delete(instance_id); return n; });
      setClasses(prev => prev.map(c => c.id === instance_id ? { ...c, going: Math.max(0, (c.going || 0) - (data?.status === "cancelled" ? 1 : 0)) } : c));
      if (member?.email) fetchMine(member.email);
      toast.success(lang === "en" ? "Cancelled." : "Afgemeld.");
    } catch (err) {
      toast.error(lang === "en" ? "Cancel failed" : "Afmelden mislukt");
    }
    setBusy(null);
  };

  const handleClick = (inst) => {
    if (!member?.email) return requireLogin();
    if (mine.has(inst.id)) return cancelRsvp(inst.id);
    if (myWaitlist.has(inst.id)) return cancelRsvp(inst.id);
    return doRsvp(inst.id, member);
  };

  const forget = () => {
    signOut();
    setMine(new Set());
    toast.success(lang === "en" ? "Signed out." : "Je bent uitgelogd.");
  };

  if (!ready || !member?.email) return null;

  return (
    <section id="rsvp" className="bg-[#EAE7E2] line-t line-b pt-10 md:pt-14 pb-24 md:pb-32" data-testid="rsvp-section">
      <div className="px-6 md:px-10">
        <div className="mb-8 flex items-center justify-end gap-4" data-testid="rsvp-member-strip">
          <span className="chip text-[#4A4A4A]">{lang === "en" ? "Signed in as" : "Ingelogd als"}</span>
          <span className="font-display uppercase text-lg text-[#0A0A0A]" data-testid="rsvp-member-name">{member.name}</span>
          <a href="/dashboard" className="chip text-[#4A4A4A] accent-hover inline-flex items-center gap-1" data-testid="rsvp-open-portal">
            Dashboard <ArrowUpRight size={12}/>
          </a>
          <button onClick={forget} className="chip text-[#4A4A4A] hover:text-[#0A0A0A] inline-flex items-center gap-1" data-testid="rsvp-sign-out">
            {lang === "en" ? "Sign out" : "Uitloggen"} <X size={12}/>
          </button>
        </div>

        {loading ? (
          <div className="chip text-[#4A4A4A]">{lang === "en" ? "Loading classes…" : "Lessen laden…"}</div>
        ) : classes.length === 0 ? (
          <div className="mt-0 pt-2 md:pt-4">
            <div className="border border-[#0A0A0A] p-10 text-center" data-testid="rsvp-empty">
              <div className="section-tag mb-2">— {lang === "en" ? "Program starts" : "Programma start"}</div>
              <div className="font-display uppercase text-4xl md:text-6xl text-[#0A0A0A]">
                {lang === "en" ? "First class · " : "Eerste les · "}<span className="font-serif-italic normal-case tracking-tight accent-ink">{lang === "en" ? "Oct 6" : "6 okt"}</span>
              </div>
              <p className="mt-4 text-[#4A4A4A] max-w-md mx-auto">
                {lang === "en"
                  ? "The full weekly programme kicks off on October 6. As a member, you can choose your date here as soon as booking opens."
                  : "Het volledige weekprogramma start op 6 oktober. Als lid kies je hier je datum zodra het boeken opent."}
              </p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 md:gap-4" data-testid="rsvp-grid">
            {classes.slice(0, 9).map((c, i) => {
              const d = fmtDate(c.date, lang);
              const going = c.going || 0;
              const spotsLeft = Math.max(0, c.capacity - going);
              const isFull = spotsLeft === 0;
              const isMine = mine.has(c.id);
              const wlPos = myWaitlist.get(c.id);
              const isWaitlisted = wlPos !== undefined;
              const inverted = isMine || isWaitlisted;
              return (
                <Rise key={c.id} delay={i * 0.05}>
                  <div className={`border p-5 md:p-6 h-full flex flex-col justify-between transition-colors ${
                    inverted ? "bg-[#0A0A0A] text-[#F5F3F0] border-[#0A0A0A]" : "bg-[#F5F3F0] text-[#0A0A0A] border-[#0A0A0A]"
                  }`} data-testid={`rsvp-card-${c.id}`}>
                    <div>
                      <div className="flex items-start justify-between">
                        <div>
                          <div className={`chip ${inverted ? "text-[#A3A3A3]" : "text-[#4A4A4A]"}`}>{d.weekday}</div>
                          <div className="flex items-baseline gap-2 mt-1">
                            <span className="font-display text-5xl leading-none">{d.day}</span>
                            <span className={`chip ${inverted ? "text-[#DDD6CB]" : "text-[#4A4A4A]"} uppercase`}>{d.month}</span>
                          </div>
                        </div>
                        <div className={`chip ${inverted ? "text-[#DDD6CB]" : "text-[#4A4A4A]"} text-right`}>
                          <div>{c.start}</div>
                          <div className="opacity-70">— {c.end}</div>
                        </div>
                      </div>

                      <div className="mt-6">
                        <div className="font-display uppercase text-2xl md:text-3xl leading-[0.95]">{c.type}</div>
                        <div className={`chip mt-2 ${inverted ? "text-[#A3A3A3]" : "text-[#4A4A4A]"}`}>{c.level}</div>
                      </div>

                      <div className={`mt-6 flex items-center gap-4 text-sm ${inverted ? "text-[#DDD6CB]" : "text-[#4A4A4A]"}`}>
                        {c.show_count && <span className="inline-flex items-center gap-1" data-testid={`rsvp-count-${c.id}`}><Users size={14}/> {going}/{c.capacity}</span>}
                        {isWaitlisted ? (
                          <span className="chip" style={{ color: "var(--joh-accent)" }} data-testid={`rsvp-waitlist-pos-${c.id}`}>
                            {lang === "en" ? `Waitlist #${wlPos}` : `Reservelijst #${wlPos}`}
                          </span>
                        ) : isFull ? (
                          <span className="chip text-red-800">{lang === "en" ? "FULL" : "VOL"}</span>
                        ) : c.show_count && spotsLeft <= 3 ? (
                          <span className="chip">{lang === "en" ? `${spotsLeft} left` : `${spotsLeft} plek over`}</span>
                        ) : null}
                      </div>

                      {isFull && !isMine && !isWaitlisted && (
                        <p className={`mt-4 text-xs md:text-sm ${inverted ? "text-[#DDD6CB]" : "text-[#4A4A4A]"}`} data-testid={`rsvp-full-notice-${c.id}`}>
                          {lang === "en"
                            ? "This class is full. Sign up for the waitlist."
                            : "Deze les is vol. Schrijf je in voor de reservelijst."}
                        </p>
                      )}
                    </div>

                    <button
                      onClick={() => handleClick(c)}
                      disabled={busy === c.id}
                      className={`mt-6 w-full justify-center ${inverted ? "btn-ghost text-[#F5F3F0] border-[#F5F3F0] hover:bg-[#F5F3F0] hover:text-[#0A0A0A]" : "btn"}`}
                      data-testid={`rsvp-btn-${c.id}`}
                    >
                      {busy === c.id ? (lang === "en" ? "…" : "…")
                        : isMine ? (<>{lang === "en" ? "Cancel" : "Afmelden"} <X size={14}/></>)
                        : isWaitlisted ? (<>{lang === "en" ? "Leave waitlist" : "Verlaat reservelijst"} <X size={14}/></>)
                        : isFull ? (<><Users size={14}/> {lang === "en" ? "Join waitlist" : "Op de reservelijst"}</>)
                        : (<><Check size={14}/> {lang === "en" ? "I'm coming" : "Ik kom naar de les"}</>)}
                    </button>
                  </div>
                </Rise>
              );
            })}
          </div>
        )}
      </div>

    </section>
  );
}
